import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";

const LINKS = [
  { to: "/programs", label: "Programs" },
  { to: "/about", label: "About" },
  { to: "/news", label: "News" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-50 border-b transition-[background-color,border-color,backdrop-filter] duration-500 ${
          scrolled
            ? "bg-background/95 backdrop-blur-xl border-border/60"
            : "bg-background/95 backdrop-blur-md border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-4 shrink-0">
            <img
              src="/assets/logo-crest.png"
              alt="Victoria Career College crest"
              className="h-16 w-auto"
            />
            <span className="hidden sm:block leading-tight">
              <span className="block font-serif text-xl font-semibold tracking-tight text-navy">
                Victoria Career College
              </span>
              <span className="block text-[10px] tracking-[0.22em] uppercase text-muted-foreground">
                of Health &amp; Business Ltd.
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8" data-testid="nav-links">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `group relative text-sm tracking-wide transition-colors duration-300 ${
                    isActive ? "text-navy" : "text-muted-foreground hover:text-navy"
                  }`
                }
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-[width] duration-500 group-hover:w-full" />
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-apply-btn"
              className="group inline-flex items-center gap-2 bg-navy text-primary-foreground text-sm px-5 py-2.5 rounded-sm transition-colors duration-300 hover:bg-accent"
            >
              Apply Now
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 text-navy"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-40 bg-navy text-primary-foreground flex flex-col justify-center px-8"
          >
            <nav className="space-y-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.5 }}
                >
                  <Link to={l.to} className="font-serif text-4xl hover:text-gold transition-colors duration-300">
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
              >
                <Link to="/contact" className="inline-flex items-center gap-2 text-gold text-lg">
                  Apply Now <ArrowUpRight size={18} />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
